import React, { useMemo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { DeliveryRoute, RouteStop } from "app/types/route";
import { DeliveryDashboardSettings } from "app/types/deliveryDashboardSettings";
import { RouteStopsTable } from "./RouteStopsTable";
import { buildPickingLookup, useRouteStopsList } from "./useRouteStopsList";

const PANEL_ICON_BUTTON_SX = {
  width: 30,
  height: 30,
  border: "1px solid #cbd5e1",
  borderRadius: "8px",
  bgcolor: "#fff",
  color: "#334155",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
  "&:hover": {
    bgcolor: "#fff",
    borderColor: "#94a3b8",
    color: "#0f172a",
    boxShadow: "0 2px 4px rgba(15, 23, 42, 0.1)",
  },
};

type RouteStopsPanelProps = {
  routeDetail: DeliveryRoute | null;
  isLoading: boolean;
  sortedStops: RouteStop[];
  accessToken: string;
  embedded?: boolean;
  dashboardSettings: DeliveryDashboardSettings | null;
  onReleased: () => void | Promise<void>;
  onClose: () => void;
  fullWidth?: boolean;
};

export function RouteStopsPanel({
  routeDetail,
  isLoading,
  sortedStops,
  accessToken,
  embedded = false,
  dashboardSettings,
  onReleased,
  onClose,
  fullWidth = false,
}: RouteStopsPanelProps) {
  const listControls = useRouteStopsList({
    stops: sortedStops,
    accessToken,
    embedded,
    dashboardSettings,
    onReleased,
  });

  const pickingById = useMemo(
    () => buildPickingLookup(routeDetail?.picking_ids),
    [routeDetail?.picking_ids]
  );

  const failedCount = listControls.releasableStops.length;

  return (
    <Box
      sx={{
        width: fullWidth ? "100%" : { xs: "100%", sm: 360, md: 400, lg: 420 },
        maxWidth: "100%",
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        borderRight: "1px solid #eef1f4",
        bgcolor: "#fff",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 1.25,
          py: 0.65,
          borderBottom: "1px solid #eef1f4",
          flexShrink: 0,
          bgcolor: "#fafbfc",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          minHeight: 40,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          {sortedStops.length > 0 && (
            <Typography
              variant="caption"
              sx={{ color: "#64748b", fontWeight: 600, pl: 0.25, flexShrink: 0 }}
            >
              {sortedStops.length} stop{sortedStops.length === 1 ? "" : "s"}
            </Typography>
          )}
          {listControls.showReleaseActions && failedCount > 1 && (
            <Button
              size="small"
              variant="outlined"
              disabled={listControls.isReleasingAll || listControls.releasingStopId != null}
              onClick={() => {
                void listControls.releaseAllFailed();
              }}
              sx={{
                textTransform: "none",
                borderColor: "#fca5a5",
                color: "#991b1b",
                fontWeight: 600,
                fontSize: "0.8125rem",
                py: 0.35,
                minWidth: 0,
                px: 1.25,
                bgcolor: "#fff",
                "&:hover": {
                  borderColor: "#f87171",
                  bgcolor: "#fef2f2",
                },
              }}
            >
              {listControls.isReleasingAll ? (
                <CircularProgress size={14} sx={{ color: "#991b1b" }} />
              ) : (
                `Release all failed (${failedCount})`
              )}
            </Button>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          {sortedStops.length > 0 && (
            <Tooltip title={listControls.allExpanded ? "Collapse all" : "Expand all"}>
              <IconButton
                size="small"
                aria-label={listControls.allExpanded ? "Collapse all stops" : "Expand all stops"}
                onClick={listControls.toggleExpandAll}
                sx={PANEL_ICON_BUTTON_SX}
              >
                {listControls.allExpanded ? (
                  <UnfoldLessIcon sx={{ fontSize: 18 }} />
                ) : (
                  <UnfoldMoreIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Close stops list">
            <IconButton
              size="small"
              aria-label="Close stops list"
              onClick={onClose}
              sx={PANEL_ICON_BUTTON_SX}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        {isLoading ? (
          <Box
            sx={{
              p: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 120,
            }}
          >
            <CircularProgress size={26} sx={{ color: "#7ec597" }} />
          </Box>
        ) : (
          <RouteStopsTable
            stops={sortedStops}
            pickingById={pickingById}
            listControls={listControls}
          />
        )}
      </Box>
    </Box>
  );
}
